import React, { useState } from 'react';
import { Card, Empty, Chevron, HowCalculated, DefinitionTable } from '../MetricsUI';
import Section from '../Section';
import { OrgLink } from './adminShared';

/*
 * Section 4 — content calibration, pooled across every organisation.
 *
 * Four views behind one tab row. Freshness is NOT a separate view: content age
 * only means something next to how much the content is being used, so it lives
 * as a column inside Curriculum, where a stale-but-heavily-used chapter is
 * visible in one glance.
 */

const TABS = [
  { key: 'cases', label: 'Case studies' },
  { key: 'avatars', label: 'AI avatars' },
  { key: 'quizzes', label: 'Quizzes' },
  { key: 'curriculum', label: 'Curriculum & freshness' },
];

const scoreColour = (pct) => {
  if (pct == null) return 'bg-gray-200';
  if (pct < 60) return 'bg-rose-400';
  if (pct < 71) return 'bg-amber-400';
  if (pct < 76) return 'bg-yellow-300';
  return 'bg-emerald-500';
};
const cellColour = (pct) => {
  if (pct == null) return 'bg-gray-50 text-gray-300';
  if (pct < 55) return 'bg-rose-300 text-rose-950';
  if (pct < 62) return 'bg-orange-300 text-orange-950';
  if (pct < 72) return 'bg-yellow-200 text-yellow-900';
  if (pct < 80) return 'bg-emerald-300 text-emerald-950';
  return 'bg-emerald-500 text-white';
};

/** Age in months, red when very stale. */
const AgeCell = ({ ageDays }) => {
  if (ageDays == null) return <span className="text-gray-300">—</span>;
  const months = Math.round(ageDays / 30);
  const tone = ageDays > 400 ? 'text-rose-600' : ageDays > 250 ? 'text-amber-600' : 'text-gray-500';
  return <span className={`text-xs tabular-nums ${tone}`}>{months}mo</span>;
};

export default function AdminContent({ data, orgsById, onOpenOrg }) {
  const [tab, setTab] = useState('cases');
  const { content } = data;
  const scored = content.caseStudies.filter((c) => c.avgPct != null);
  const quizScored = content.quizzes.filter((q) => q.avgFirstPct != null);
  const reached = content.chapters.filter((c) => c.startedPct > 0).length;

  return (
    <Section
      title="Content"
      subtitle="Which material the platform has reached, and which material is fighting back"
      summary={
        <div className="flex flex-wrap gap-4">
          <Tile
            label="Chapters reached"
            value={reached}
            unit={`/ ${content.chapters.length}`}
            help="Chapters where at least one learner anywhere on the platform has started something. Chapters holding no lessons, quizzes or case studies are excluded."
          />
          <Tile
            label="Case studies scored"
            value={scored.length}
            unit={`/ ${content.caseStudies.length}`}
            help="Case studies with at least one scored attempt anywhere. The rest have never been completed by anyone, which is different from being hard."
          />
          <Tile
            label="Quizzes scoreable"
            value={quizScored.length}
            unit={`/ ${content.quizzes.length}`}
            help="Quizzes whose recorded scores can be believed. A quiz still written as 1 out of 1 is excluded — counting it would read as a perfect score."
          />
          <Tile
            label="Avatar data"
            value={content.avatarMatrix.available ? 'Recording' : 'Not yet'}
            hint={content.avatarMatrix.available ? undefined : 'needs the next Unity build'}
            help="Whether any attempt records which AI personality was faced. The matrix is wired either way and fills in on its own when the data starts arriving."
          />
          <Tile
            label="Lesson watch data"
            value={content.lessonWatchAvailable ? 'Recording' : 'Not yet'}
            hint={content.lessonWatchAvailable ? undefined : 'needs the next Unity build'}
            help="Whether attempts carry how far through a lesson the learner got. Slides, views and content age are real today; reached-end and average watched are not."
          />
        </div>
      }
    >
      <Card>
        <div className="mb-4 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                tab === t.key ? 'bg-navy text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'cases' && (
          <ModuleDifficulty
            rows={content.caseStudies}
            metric="avgPct"
            tickMetric="avgFirstPct"
            title="Case study difficulty — hardest first"
            orgsById={orgsById}
            onOpenOrg={onOpenOrg}
            help={[
              ['Bar', 'Mean score over every scored attempt on the platform'],
              ['Dark tick', "Mean of each learner's FIRST scored attempt"],
              ['Gap', 'Tick well left of the bar means the case teaches well on a retry'],
              ['Orgs', 'How many customers have ever scored it — hover to list them'],
              ['Order', 'Lowest average first; unattempted cases sort last'],
            ]}
          />
        )}

        {tab === 'quizzes' && (
          <ModuleDifficulty
            rows={content.quizzes.filter((q) => q.avgFirstPct != null)}
            metric="avgFirstPct"
            title="Quiz difficulty — lowest first attempt"
            emptyLabel="No quiz score can be trusted yet — quizzes are still recorded as 1 out of 1. This fills in with the next Unity build."
            orgsById={orgsById}
            onOpenOrg={onOpenOrg}
            help={[
              ['Bar', "Mean of each learner's FIRST attempt at that quiz"],
              ['Why first', 'A retry measures persistence, not whether the lesson landed'],
              ['Excluded', 'Quizzes recorded as 1 out of 1, which would read as 100%'],
              ['Orgs', 'Customers whose learners have taken it — hover to list them'],
            ]}
          />
        )}

        {tab === 'avatars' && <AvatarMatrix matrix={content.avatarMatrix} />}

        {tab === 'curriculum' && (
          <Curriculum
            chapters={content.chapters}
            watchAvailable={content.lessonWatchAvailable}
            orgsById={orgsById}
            onOpenOrg={onOpenOrg}
          />
        )}
      </Card>
    </Section>
  );
}

function Tile({ label, value, unit, hint, help }) {
  return (
    <div className="min-w-[150px] flex-1 rounded-lg bg-white p-4 shadow">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
        {help && <HowCalculated>{help}</HowCalculated>}
      </p>
      <p className="mt-1 text-2xl font-bold text-navy">
        {value}
        {unit && <span className="ml-1 text-base font-semibold text-gray-400">{unit}</span>}
      </p>
      {hint && <p className="mt-0.5 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

/** Bar per module, hardest first, hovering to the orgs that use it. */
function ModuleDifficulty({ rows, metric, tickMetric, title, help, emptyLabel, orgsById, onOpenOrg }) {
  const [hover, setHover] = useState(null);

  if (rows.length === 0) return <Empty>{emptyLabel || 'Nothing scored yet.'}</Empty>;

  return (
    <div>
      <p className="mb-3 text-sm font-medium text-gray-700">
        {title}
        <HowCalculated wide label="What is plotted">
          <DefinitionTable rows={help} />
        </HowCalculated>
      </p>

      {rows.map((r) => (
        <div
          key={r.moduleIdSst}
          className="flex items-center gap-3 py-1"
          onMouseEnter={() => setHover(r)}
          onMouseLeave={() => setHover(null)}
        >
          <div className="w-[210px] shrink-0 truncate text-sm text-gray-700" title={r.moduleName}>
            {r.moduleName}
          </div>
          <div className="relative h-5 flex-1 overflow-hidden rounded bg-gray-100">
            {r[metric] != null && (
              <div
                className={`h-full rounded ${scoreColour(r[metric])}`}
                style={{ width: `${Math.max(0, Math.min(100, r[metric]))}%` }}
              />
            )}
            {tickMetric && r[tickMetric] != null && (
              <div
                className="absolute top-0 h-full w-[2px] bg-navy/70"
                style={{ left: `${Math.max(0, Math.min(100, r[tickMetric]))}%` }}
                title={`first attempt ${Math.round(r[tickMetric])}%`}
              />
            )}
          </div>
          <div className="w-12 shrink-0 text-right text-sm font-bold tabular-nums text-navy">
            {r[metric] == null ? '—' : `${Math.round(r[metric])}%`}
          </div>
          <div className="w-24 shrink-0 text-right text-xs tabular-nums text-gray-400">
            {r.orgCount} org{r.orgCount === 1 ? '' : 's'} · {r.people}
          </div>
        </div>
      ))}

      {/* Fixed height so the list does not jump as the pointer moves. */}
      <div className="mt-2 min-h-[34px]">
        {hover && hover.orgs.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="mr-1 font-medium text-gray-500">{hover.moduleName}:</span>
            {hover.orgs.map((id) => (
              <span
                key={id}
                className="rounded border border-gray-200 px-1.5 py-0.5 hover:border-lavender hover:bg-indigo-50/50"
              >
                <OrgLink org={orgsById.get(id) || { name: 'Unknown', organizationId: id }} onOpenOrg={onOpenOrg} />
              </span>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-gray-400">Hover a row to list the organisations using it.</p>
        )}
      </div>
    </div>
  );
}

function AvatarMatrix({ matrix }) {
  const [hover, setHover] = useState(null);
  const { avatars, rows, perAvatar, available } = matrix;

  return (
    <div>
      <p className="mb-3 text-sm font-medium text-gray-700">
        Case study &times; AI avatar
        <HowCalculated wide label="What is plotted">
          <DefinitionTable
            rows={[
              ['Cell', 'Mean score for that case study against that avatar, platform-wide'],
              ['Colour', 'Red = harder, green = easier'],
              ['Dash', 'Nobody has played that pairing'],
              ['Cards below', "Each avatar's overall average, median and attempt count"],
              ['Why it matters', 'The same case against a different personality is a different rehearsal'],
            ]}
          />
        </HowCalculated>
      </p>

      {!available && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          No attempt on the platform records which avatar was faced, so every cell is blank. The chart is
          wired and fills in on its own once the next Unity build starts sending it.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-separate" style={{ borderSpacing: '3px' }}>
          <thead>
            <tr>
              <th className="w-[190px]" />
              {avatars.map((a) => (
                <th key={a} className="px-2 pb-1 text-center text-xs font-semibold text-gray-600">
                  {a}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.moduleIdSst}>
                <td className="w-[190px] truncate pr-2 text-sm text-gray-700" title={r.moduleName}>
                  {r.moduleName}
                </td>
                {r.cells.map((c) => (
                  <td key={c.avatarName} className="p-0">
                    <div
                      onMouseEnter={() => setHover({ row: r, cell: c })}
                      onMouseLeave={() => setHover(null)}
                      className={`flex h-8 items-center justify-center rounded text-xs font-semibold tabular-nums ${cellColour(
                        c.avgPct
                      )} ${hover && hover.cell === c ? 'ring-2 ring-navy' : ''}`}
                    >
                      {c.avgPct == null ? '—' : Math.round(c.avgPct)}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex min-h-[20px] items-center justify-between text-xs">
        <span>
          {hover ? (
            <>
              <b className="text-navy">{hover.row.moduleName}</b>
              <span className="text-gray-500"> vs </span>
              <b className="text-navy">{hover.cell.avatarName}</b>
              <span className="text-gray-500">
                {hover.cell.avgPct == null
                  ? ' — not attempted'
                  : ` — avg ${Math.round(hover.cell.avgPct)}% over ${hover.cell.attempts} attempt${
                      hover.cell.attempts === 1 ? '' : 's'
                    }`}
              </span>
            </>
          ) : (
            <span className="text-gray-400">Hover a cell for its detail.</span>
          )}
        </span>
        <span className="flex items-center gap-1.5 text-[10px] text-gray-400">
          harder
          <span className="h-3 w-4 rounded bg-rose-300" />
          <span className="h-3 w-4 rounded bg-orange-300" />
          <span className="h-3 w-4 rounded bg-yellow-200" />
          <span className="h-3 w-4 rounded bg-emerald-300" />
          <span className="h-3 w-4 rounded bg-emerald-500" />
          easier
        </span>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {perAvatar.map((a) => (
          <div key={a.avatarName} className="rounded-lg border border-gray-100 p-3">
            <p className="text-sm font-semibold text-navy">{a.avatarName}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-navy">
              {a.avgPct == null ? '—' : `${Math.round(a.avgPct)}%`}
            </p>
            <p className="text-[11px] text-gray-400">
              {a.attempts} attempt{a.attempts === 1 ? '' : 's'}
              {a.medianPct != null && ` · median ${Math.round(a.medianPct)}%`}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Chapter reach with freshness, expanding to the lessons inside.
 *
 * This is where the prototype's separate "Freshness" and "Lessons" views merge.
 * Age on its own is not actionable — a two-year-old chapter nobody opens does not
 * matter, and a two-year-old chapter everyone reaches does. Putting reach and age
 * on the same row makes that comparison immediate, and the lessons behind the
 * chevron are where the drop-off actually happens.
 */
function Curriculum({ chapters, watchAvailable, orgsById, onOpenOrg }) {
  const [openId, setOpenId] = useState(null);

  return (
    <div>
      <p className="mb-3 text-sm font-medium text-gray-700">
        Chapter reach &amp; freshness
        <HowCalculated wide label="What is plotted">
          <DefinitionTable
            rows={[
              ['Bar', 'Learners who have started the chapter, of those who have it in scope'],
              ['Dark tick', 'Learners who have completed it, on the same scale'],
              ['n', 'Learners with this chapter in their learning path, across all orgs'],
              ['Age', 'Months since the chapter record was last updated'],
              ['Open a row', 'The lessons inside, with their own age and usage'],
            ]}
          />
          <span className="block text-gray-500">
            The denominator is per chapter, so an advanced cohort skipping a chapter is not a drop-off.
          </span>
        </HowCalculated>
      </p>

      {!watchAvailable && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Per-lesson watch progress is not recorded on any attempt yet, so &ldquo;reached the end&rdquo; and
          &ldquo;avg watched&rdquo; show as dashes. Slides, views and content age are real. The columns fill
          in once the next Unity build ships.
        </div>
      )}

      {chapters.length === 0 && <Empty>No chapters in scope.</Empty>}

      <div className="max-h-[560px] overflow-y-auto pr-1">
        {chapters.map((c) => (
          <div key={c.chapterId} className="border-b border-gray-100 last:border-0">
            <button
              onClick={() => setOpenId(openId === c.chapterId ? null : c.chapterId)}
              className="flex w-full items-center gap-3 py-1.5 text-left hover:bg-gray-50"
            >
              <span className="w-4 shrink-0">
                {c.lessons.length > 0 && <Chevron open={openId === c.chapterId} />}
              </span>
              <span className="w-[210px] shrink-0 truncate text-sm text-gray-700" title={c.title}>
                {c.number}. {c.title}
              </span>
              <span className="relative h-5 flex-1 overflow-hidden rounded bg-gray-100">
                <span
                  className="block h-full rounded bg-lavender"
                  style={{ width: `${Math.max(0, Math.min(100, c.startedPct))}%` }}
                />
                <span
                  className="absolute top-0 h-full w-[2px] bg-navy/70"
                  style={{ left: `${Math.max(0, Math.min(100, c.completedPct))}%` }}
                  title={`${c.completedPct}% completed`}
                />
              </span>
              <span className="w-12 shrink-0 text-right text-sm font-bold tabular-nums text-navy">
                {c.startedPct}%
              </span>
              <span className="w-14 shrink-0 text-right text-xs tabular-nums text-gray-400">
                n={c.inScope}
              </span>
              <span className="w-14 shrink-0 text-right">
                <AgeCell ageDays={c.ageDays} />
              </span>
              <span className="w-20 shrink-0 text-right text-xs tabular-nums text-gray-400">
                {c.lessons.length} lesson{c.lessons.length === 1 ? '' : 's'}
              </span>
            </button>

            {openId === c.chapterId && c.lessons.length > 0 && (
              <div className="mb-2 ml-7 overflow-x-auto rounded-lg bg-gray-50 p-3">
                <table className="w-full min-w-[620px]">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      <th className="pb-1">Lesson</th>
                      <th className="w-16 pb-1 text-right">Slides</th>
                      <th className="w-16 pb-1 text-right">Views</th>
                      <th className="w-20 pb-1 text-right">Learners</th>
                      <th className="w-24 pb-1 text-right">Reached end</th>
                      <th className="w-24 pb-1 text-right">Avg watched</th>
                      <th className="w-16 pb-1 text-right">Age</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {c.lessons.map((l) => (
                      <tr key={l.moduleIdSst} className="text-sm">
                        <td className="py-1.5 pr-2 text-gray-700">{l.moduleName}</td>
                        <td className="py-1.5 text-right tabular-nums text-gray-600">{l.slides ?? '—'}</td>
                        <td className="py-1.5 text-right tabular-nums text-gray-600">{l.views || '—'}</td>
                        <td className="py-1.5 text-right tabular-nums text-gray-600">{l.people || '—'}</td>
                        <td className="py-1.5 text-right tabular-nums text-gray-400">
                          {l.reachedEndPct == null ? '—' : `${Math.round(l.reachedEndPct)}%`}
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-gray-400">
                          {l.avgWatchedPct == null ? '—' : `${Math.round(l.avgWatchedPct)}%`}
                        </td>
                        <td className="py-1.5 text-right">
                          <AgeCell ageDays={l.ageDays} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
